/* eslint-disable no-restricted-globals */

import React, { useCallback, useEffect, useState } from "react";
import { Map, TravelTimeline } from "@atoms/index";
import { Button, Dialog, DialogActions } from "@libraries/mui";
import "react-step-progress-bar/styles.css";
import "react-vertical-timeline-component/style.min.css";
import { ReactSVG } from "react-svg";
import utils from "@helpers/utils";
import { intervalToDuration } from "date-fns";
import { useQuery } from "@apollo/client";

// icons
import { Construction, Discharging, Transit } from "@assets/pins";

import { GpsSignal } from "@assets/icons/index";

import {
  // eslint-disable-next-line camelcase
  ETravelStatus,
  TravelQueriesGetPositionByExternalIdArgs,
  TravelType
} from "@store/graphql/helpers/types.graphql";
import {
  positionByExternalId,
  TVehiclePositionByExternalIdQueries
} from "src/store/graphql/groups/travel/query.graphql";
import { TravelMarkerType } from "src/atoms/Map";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui/dist";
import { TelegramIcon, TelegramShareButton, WhatsappIcon, WhatsappShareButton } from "react-share";
import { api } from "@store/services/rest";
import { ECommandResult } from "@store/enums/commandResult.enum";
import { ConcretingMessages, GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
import * as Styled from "./styles";

// types

interface TravelDialogProps {
  open: boolean;
  handleClose: () => void;
  travel: TravelType;
  header: React.ReactNode;
  // eslint-disable-next-line react/require-default-props
  sharing?: boolean;
  concretingId?: string;
}
const TravelDialog: React.FC<TravelDialogProps> = ({
  open,
  handleClose,
  travel,
  header,
  sharing = true,
  concretingId
}) => {
  const { addToast } = useToast();
  const { t } = useTranslation();

  const [marker, setMarker] = useState<TravelMarkerType | null>(null);
  const [lastSendDate, setLastSendDate] = useState<Date | null>(null);
  const [travelSharing, setTravelSharing] = useState(true);
  const [shareUrl, setShareUrl] = useState("");

  const {
    loading: loadingVehiclePosition,
    error: errorVehiclePosition,
    data: dataVehiclePosition
  } = useQuery<
    TVehiclePositionByExternalIdQueries,
    TravelQueriesGetPositionByExternalIdArgs
  >(positionByExternalId(["lastSendDate", "lastLatitude", "lastLongitude"]), {
    fetchPolicy: "network-only",
    pollInterval: 60000,
    skip: travel.status === ETravelStatus.DELIVERED,
    variables: {
      externalId: travel.fleetExternalId ?? ""
    }
  });

  const title = t(ConcretingMessages.trackDelivery);

  const getToken = useCallback(async () => {
    const response = await api.post("/auth/anonymouslogin", {});

    // eslint-disable-next-line camelcase
    const responseData = response?.data;

    if (responseData.status === ECommandResult.SUCCESS) {
      const token = responseData.message;
      const baseUrl = `${location.protocol}//${location.host}`;
      const url = `${baseUrl}/customer/travel-sharing?token=${token}&concretingId=${concretingId}&travelId=${travel.id}`;

      setShareUrl(url);
    }
  }, []);

  const handleShareWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`, "_blank");
  };

  const handleShareTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`, "_blank");
  };

  useEffect(() => {
    getToken().then();
  }, [getToken]);

  useEffect(() => {
    const response = dataVehiclePosition?.travel.getPositionByExternalId;

    if (response?.result) {

      if (response.result.lastSendDate) {
        let icon = Transit;

        if (travel.status === "IN_CONSTRUCTION") icon = Construction;
        else if (
          travel.status === "DISCHARGING"
          || travel.status === "DELIVERED"
        ) icon = Discharging;

        setMarker({
          travel,
          marker: {
            position: {
              lat: Number.parseFloat(response.result?.lastLatitude ?? "0"),
              lng: Number.parseFloat(response.result?.lastLongitude ?? "0")
            },
            icon: {
              url: icon,
              scaledSize: { width: 30, height: 45 } as google.maps.Size
            }
          }
        });

        setLastSendDate(new Date(response.result.lastSendDate));
      } else {
        addToast({
          type: "info",
          title: "Aviso",
          description: "Não foi possível obter a posição do veículo"
        });
      }
    }
    if (travel.status !== "DELIVERED") {
      setTravelSharing(true);
    }
  }, [addToast, dataVehiclePosition, travel.status]);

  return (
    <Dialog id="travel-modal" open={open} onClose={handleClose}>
      <Styled.ModalHeader>
        {header}
        {travelSharing && sharing && shareUrl && window.innerWidth > 400 && (
          <div className="sharing">
            <WhatsappShareButton url={shareUrl} title={title}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <TelegramShareButton url={shareUrl} title={title}>
              <TelegramIcon size={32} round />
            </TelegramShareButton>
          </div>
        )}
        {travelSharing && sharing && shareUrl && window.innerWidth < 400 && (
        <div className="sharing">
          <Styled.ButtonHandle type="button" onClick={handleShareWhatsapp}>
            <WhatsappIcon size={32} round />
          </Styled.ButtonHandle>
          <Styled.ButtonHandle type="button" onClick={handleShareTelegram}>
            <TelegramIcon size={32} round />
          </Styled.ButtonHandle>
        </div>
        )}
      </Styled.ModalHeader>
      {marker && (
        <Styled.MapContainer>
          <Map travelMarkers={[marker]} />
        </Styled.MapContainer>
      )}
      <Styled.DialogContent>

        <TravelTimeline travel={travel} />
        {lastSendDate && (
          <div className="communication">
            <ReactSVG src={GpsSignal} />
            Última atualização{" "}
            {utils.formatDateIfHave(lastSendDate, "dateWithoutYear")}
            {" à "}
            {utils.formatDateIfHave(
              intervalToDuration({
                start: lastSendDate,
                end: new Date()
              }),
              "durationDescriptiveTime"
            )}
            {" atrás"}
          </div>
        )}
      </Styled.DialogContent>
      {sharing && (
        <DialogActions>
          <Styled.DialogActions>
            <Button onClick={handleClose}>{t(GlobalMessages.buttonClose)}</Button>
          </Styled.DialogActions>
        </DialogActions>
      )}
    </Dialog>
  );
};

TravelDialog.defaultProps = {
  concretingId: undefined
};

export default TravelDialog;